import { useState, type FormEvent } from 'react'
import * as stylex from '@stylexjs/stylex'

import Avatar from '../../components/Avatar'
import AvatarEditor from '../../components/AvatarEditor'
import Button from '../../components/Button'
import { FormField, Input, Select } from '../../components/FormControls'
import SplitwiseImportCard from '../../components/SplitwiseImportCard'
import { currencyOptions, type DatabaseSyncState, type ImportPreview, type ProfileSettingsInput, type User } from '../../lib/ledger'

const styles = stylex.create({
  page: { display: 'flex', flexDirection: 'column', rowGap: '24px', paddingBottom: '32px' },
  back: { alignSelf: 'flex-start', color: '#55735d', fontSize: '13px', fontWeight: 600, ':hover': { color: '#2f6f42' } },
  card: { borderWidth: 1, borderColor: '#e1e8df', borderRadius: '1rem', backgroundColor: '#fff', padding: '24px' },
  header: { display: 'flex', alignItems: 'center', gap: '16px' },
  avatarButton: { display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: '6px', borderRadius: '9999px', color: '#55735d', ':hover': { color: '#2f6f42' } },
  avatarHint: { fontSize: '11px', fontWeight: 600 },
  fileInput: { position: 'absolute', height: '1px', width: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  title: { marginTop: '20px', fontSize: '24px', fontWeight: 600 },
  subtitle: { marginTop: '6px', color: '#748077', fontSize: '13px' },
  section: { display: 'flex', flexDirection: 'column', rowGap: '20px' },
  sectionHeader: { display: 'flex', flexDirection: 'column', rowGap: '6px' },
  sectionTitle: { fontSize: '18px', fontWeight: 600 },
  sectionDescription: { color: '#748077', fontSize: '13px', lineHeight: '1.35rem' },
  fields: { display: 'grid', gap: '16px', '@media (min-width: 640px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } },
  field: { display: 'flex', flexDirection: 'column', rowGap: '8px' },
  fieldLabel: { color: '#3d463f', fontSize: '13px', fontWeight: 600 },
  input: { height: '44px', width: '100%', borderWidth: 1, borderColor: '#dbe2d9', borderRadius: '0.75rem', backgroundColor: '#fff', paddingInline: '12px', fontSize: '14px', outline: 'none', ':focus': { borderColor: '#5b9a6c', boxShadow: '0 0 0 3px rgb(91 154 108 / 12%)' } },
  select: { height: '44px', width: '100%', borderWidth: 1, borderColor: '#dbe2d9', borderRadius: '0.75rem', backgroundColor: '#fff', paddingInline: '12px', fontSize: '14px', outline: 'none', ':focus': { borderColor: '#5b9a6c', boxShadow: '0 0 0 3px rgb(91 154 108 / 12%)' } },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTopWidth: 1, borderColor: '#e8ece7', paddingTop: '18px' },
  primary: { height: '42px', borderRadius: '0.75rem', backgroundColor: '#408b56', paddingInline: '18px', fontSize: '13px', fontWeight: 600, color: '#fff', ':hover': { backgroundColor: '#347c48' }, ':disabled': { cursor: 'not-allowed', opacity: 0.6 } },
  danger: { backgroundColor: '#b84a45', ':hover': { backgroundColor: '#9f3e3a' } },
  error: { borderRadius: '0.75rem', backgroundColor: '#fff3ed', padding: '10px 12px', color: '#9b491e', fontSize: '12px' },
  success: { borderRadius: '0.75rem', backgroundColor: '#edf7eb', padding: '10px 12px', color: '#397a4b', fontSize: '12px' },
  ratePrefix: { display: 'flex', height: '44px', alignItems: 'center', borderWidth: 1, borderColor: '#dbe2d9', borderRadius: '0.75rem', backgroundColor: '#fff', paddingLeft: '12px', color: '#68806d', fontSize: '13px', fontWeight: 600, ':focus-within': { borderColor: '#5b9a6c', boxShadow: '0 0 0 3px rgb(91 154 108 / 12%)' } },
  rateInput: { minWidth: '0px', flex: '1 1 0%', height: '100%', paddingInline: '8px', fontSize: '14px', outline: 'none' },
  rateSuffix: { paddingRight: '12px', color: '#68806d', fontSize: '13px', fontWeight: 600 },
  adminCard: { borderColor: '#d9e8d7', backgroundColor: '#f7fbf6' },
  adminCopy: { color: '#66756a', fontSize: '13px', lineHeight: '1.4rem' },
  adminMeta: { color: '#52735a', fontSize: '12px', fontWeight: 600 },
  adminActions: { display: 'flex', justifyContent: 'flex-start', paddingTop: '4px' },
  passkeyCard: { borderColor: '#d9e8d7', backgroundColor: '#f7fbf6' },
})

function initialRate(user: User, key: string, fallback: number) {
  const stored = user.exchangeRates?.[key]
  return stored && stored > 0 ? (stored / 10000).toFixed(2) : fallback.toFixed(2)
}

function rateBps(value: string) {
  const parsed = Number(value.replace(',', '.'))
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 10000) : 0
}

type ProfilePageProps = {
  user: User
  onBack: () => void
  onSaveProfile: (input: ProfileSettingsInput) => Promise<void>
  onChangePassword: (currentPassword: string, newPassword: string) => Promise<void>
  syncState?: DatabaseSyncState | null
  onSyncProductionData?: () => void
  onRegisterPasskey?: () => Promise<void>
  onOpenAdmin?: () => void
  onPreviewSplitwiseImport: (file: File) => Promise<ImportPreview>
  onCommitSplitwiseImport: (importID: string) => Promise<void>
  onDiscardSplitwiseImport: (importID: string) => Promise<void>
}

export default function ProfilePage({ user, onBack, onSaveProfile, onChangePassword, syncState, onSyncProductionData, onRegisterPasskey, onOpenAdmin, onPreviewSplitwiseImport, onCommitSplitwiseImport, onDiscardSplitwiseImport }: ProfilePageProps) {
  const [avatar, setAvatar] = useState(user.avatar ?? '')
  const [email, setEmail] = useState(user.email)
  const [currency, setCurrency] = useState(user.currency)
  const [usdToAwg, setUsdToAwg] = useState(initialRate(user, 'USD_AWG', 1.8))
  const [eurToAwg, setEurToAwg] = useState(initialRate(user, 'EUR_AWG', 2.1))
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileError, setProfileError] = useState('')
  const [profileSaved, setProfileSaved] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSaved, setPasswordSaved] = useState(false)
  const [passkeySaving, setPasskeySaving] = useState(false)
  const [passkeyError, setPasskeyError] = useState('')
  const [passkeySaved, setPasskeySaved] = useState(false)

  function selectAvatar(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/') || file.size > 600 * 1024) {
      setProfileError('Choose an image smaller than 600 KB.')
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setAvatar(reader.result)
        setProfileError('')
        setProfileSaved(false)
      }
    }
    reader.readAsDataURL(file)
  }

  async function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const usdRate = rateBps(usdToAwg)
    const eurRate = rateBps(eurToAwg)
    if (!email.trim()) {
      setProfileError('Email is required.')
      return
    }
    if (!usdRate || !eurRate) {
      setProfileError('Enter a positive exchange rate for both currencies.')
      return
    }
    setProfileSaving(true)
    setProfileError('')
    setProfileSaved(false)
    try {
      await onSaveProfile({ email: email.trim(), currency, avatar, exchangeRates: { USD_AWG: usdRate, EUR_AWG: eurRate } })
      setProfileSaved(true)
    } catch (reason) {
      setProfileError(reason instanceof Error ? reason.message : 'Could not save your profile settings.')
    } finally {
      setProfileSaving(false)
    }
  }

  async function savePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!currentPassword || newPassword.length < 8) {
      setPasswordError('Enter your current password and a new password of at least 8 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('The new passwords do not match.')
      return
    }
    setPasswordSaving(true)
    setPasswordError('')
    setPasswordSaved(false)
    try {
      await onChangePassword(currentPassword, newPassword)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordSaved(true)
    } catch (reason) {
      setPasswordError(reason instanceof Error ? reason.message : 'Could not change your password.')
    } finally {
      setPasswordSaving(false)
    }
  }

  async function registerPasskey() {
    if (!onRegisterPasskey) return
    setPasskeySaving(true)
    setPasskeyError('')
    setPasskeySaved(false)
    try {
      await onRegisterPasskey()
      setPasskeySaved(true)
    } catch (reason) {
      setPasskeyError(reason instanceof Error ? reason.message : 'Could not register this passkey.')
    } finally {
      setPasskeySaving(false)
    }
  }

  const syncEnvironment = syncState?.environment === 'development' || syncState?.environment === 'staging' ? syncState.environment : ''
  const canSyncProduction = Boolean(syncState?.canSyncFromProduction && syncEnvironment && onSyncProductionData)
  const syncEnvironmentLabel = syncEnvironment === 'development' ? 'Development' : 'Staging'

  return <div {...stylex.props(styles.page)}>
    <Button onClick={onBack} size="sm" variant="ghost">← Back to overview</Button>
    <section {...stylex.props(styles.card)}>
      <div {...stylex.props(styles.header)}>
        <label aria-label="Change avatar" {...stylex.props(styles.avatarButton)}>
          <Avatar person={{ ...user, avatar }} size={64} />
          <span {...stylex.props(styles.avatarHint)}>Change avatar</span>
          <input accept="image/*" {...stylex.props(styles.fileInput)} onChange={selectAvatar} type="file" />
        </label>
        <div><p {...stylex.props(styles.sectionDescription)}>Your profile</p><h1 {...stylex.props(styles.title)}>{user.name}</h1><p {...stylex.props(styles.subtitle)}>Your SettleUp account details</p></div>
      </div>
    </section>

    <form {...stylex.props(styles.card, styles.section)} onSubmit={saveProfile}>
      <div {...stylex.props(styles.sectionHeader)}><h2 {...stylex.props(styles.sectionTitle)}>Profile preferences</h2><p {...stylex.props(styles.sectionDescription)}>Update your contact details and the settings used throughout your account.</p></div>
      <AvatarEditor key={`${user.name}:${avatar}`} name={user.name} onChange={(next) => { setAvatar(next); setProfileSaved(false) }} value={avatar} />
      <div {...stylex.props(styles.fields)}>
        <FormField label="Email"><Input autoComplete="email" onChange={(event) => setEmail(event.target.value)} type="email" value={email} /></FormField>
        <FormField label="Default currency"><Select onChange={(event) => setCurrency(event.target.value)} value={currency}>{currencyOptions(currency).map((code) => <option key={code} value={code}>{code}</option>)}</Select></FormField>
      </div>
      <div {...stylex.props(styles.sectionHeader)}><h3 {...stylex.props(styles.sectionTitle)}>Foreign exchange rates</h3><p {...stylex.props(styles.sectionDescription)}>These rates are used as the starting value when you record a currency exchange. You can still adjust a rate for an individual exchange.</p></div>
      <div {...stylex.props(styles.fields)}>
        <label {...stylex.props(styles.field)}><span {...stylex.props(styles.fieldLabel)}>USD to AWG</span><div {...stylex.props(styles.ratePrefix)}><span>1 USD =</span><input {...stylex.props(styles.rateInput)} inputMode="decimal" onChange={(event) => setUsdToAwg(event.target.value)} value={usdToAwg} /><span {...stylex.props(styles.rateSuffix)}>AWG</span></div></label>
        <label {...stylex.props(styles.field)}><span {...stylex.props(styles.fieldLabel)}>EUR to AWG</span><div {...stylex.props(styles.ratePrefix)}><span>1 EUR =</span><input {...stylex.props(styles.rateInput)} inputMode="decimal" onChange={(event) => setEurToAwg(event.target.value)} value={eurToAwg} /><span {...stylex.props(styles.rateSuffix)}>AWG</span></div></label>
      </div>
      {profileError && <p {...stylex.props(styles.error)} role="alert">{profileError}</p>}
      {profileSaved && <p {...stylex.props(styles.success)} role="status">Profile preferences saved.</p>}
      <div {...stylex.props(styles.actions)}><Button disabled={profileSaving} type="submit">{profileSaving ? 'Saving…' : 'Save preferences'}</Button></div>
    </form>


    <SplitwiseImportCard onCommit={onCommitSplitwiseImport} onDiscard={onDiscardSplitwiseImport} onPreview={onPreviewSplitwiseImport} />

    <form {...stylex.props(styles.card, styles.section)} onSubmit={savePassword}>
      <div {...stylex.props(styles.sectionHeader)}><h2 {...stylex.props(styles.sectionTitle)}>Change password</h2><p {...stylex.props(styles.sectionDescription)}>Use a new password you do not reuse elsewhere.</p></div>
      <div {...stylex.props(styles.fields)}>
        <FormField label="Current password"><Input autoComplete="current-password" onChange={(event) => setCurrentPassword(event.target.value)} type="password" value={currentPassword} /></FormField>
        <FormField label="New password"><Input autoComplete="new-password" minLength={8} onChange={(event) => setNewPassword(event.target.value)} type="password" value={newPassword} /></FormField>
        <FormField label="Confirm new password"><Input autoComplete="new-password" onChange={(event) => setConfirmPassword(event.target.value)} type="password" value={confirmPassword} /></FormField>
      </div>
      {passwordError && <p {...stylex.props(styles.error)} role="alert">{passwordError}</p>}
      {passwordSaved && <p {...stylex.props(styles.success)} role="status">Password changed successfully.</p>}
      <div {...stylex.props(styles.actions)}><Button disabled={passwordSaving} type="submit">{passwordSaving ? 'Changing…' : 'Change password'}</Button></div>
    </form>

    {onRegisterPasskey && <section aria-labelledby="passkey-heading" {...stylex.props(styles.card, styles.section, styles.passkeyCard)}>
      <div {...stylex.props(styles.sectionHeader)}><h2 id="passkey-heading" {...stylex.props(styles.sectionTitle)}>Passkeys</h2><p {...stylex.props(styles.sectionDescription)}>Add this device's passkey so you can sign in without typing your password.</p></div>
      {passkeyError && <p {...stylex.props(styles.error)} role="alert">{passkeyError}</p>}
      {passkeySaved && <p {...stylex.props(styles.success)} role="status">Passkey registered successfully.</p>}
      <div {...stylex.props(styles.adminActions)}><Button disabled={passkeySaving} onClick={() => void registerPasskey()}>{passkeySaving ? 'Waiting for passkey…' : 'Add a passkey'}</Button></div>
    </section>}

    {user.role === 'admin' && onOpenAdmin && <section aria-labelledby="admin-console-heading" {...stylex.props(styles.card, styles.section, styles.adminCard)}>
      <div {...stylex.props(styles.sectionHeader)}><h2 id="admin-console-heading" {...stylex.props(styles.sectionTitle)}>Account administration</h2><p {...stylex.props(styles.adminCopy)}>View all accounts, reset passwords, and revoke active sessions.</p></div>
      <div {...stylex.props(styles.adminActions)}><Button onClick={onOpenAdmin}>Open admin console</Button></div>
    </section>}

    {canSyncProduction && <section aria-labelledby="admin-heading" {...stylex.props(styles.card, styles.section, styles.adminCard)}>
      <div {...stylex.props(styles.sectionHeader)}><h2 id="admin-heading" {...stylex.props(styles.sectionTitle)}>Admin</h2><p {...stylex.props(styles.adminCopy)}>Manage this environment using the current production data. Syncing replaces all {syncEnvironmentLabel.toLowerCase()} data; production is never modified.</p></div>
      <p {...stylex.props(styles.adminMeta)}>Current environment: {syncEnvironmentLabel}</p>
      <div {...stylex.props(styles.adminActions)}><Button onClick={onSyncProductionData} variant="danger">Sync production data</Button></div>
    </section>}
  </div>
}
